/**
 * Minimal RFC 4180 CSV reader for the workspace viewer.
 *
 * Why not `content.split('\n').map(l => l.split(','))`: the files this renders are
 * audience exports whose columns include job titles and company names, and those
 * routinely contain commas ("Director, Platform Engineering"). A naive split shifts
 * every later column by one on exactly those rows — a wrong table that still looks
 * like a table, which is worse than refusing to render.
 *
 * Handles: quoted fields, embedded commas and newlines, `""` as an escaped quote,
 * CRLF, and a leading UTF-8 BOM. The exporter writes `utf-8-sig` on purpose so
 * Excel keeps Chinese names, so the BOM is the normal case here, not an edge one —
 * left in place it becomes part of the first header cell.
 */

/** Parse CSV text into rows of raw cell strings. Blank trailing line is dropped. */
export function parseCsv(text: string): string[][] {
  // Strip the BOM only at position 0; a U+FEFF anywhere else is real content.
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  // Per RFC 4180 a `"` is special ONLY at the start of a field. Without this flag a
  // quote anywhere in an unquoted field opens a quoted run, and since the run then
  // swallows commas and newlines until the next quote, ONE such cell collapses the
  // whole rest of the file into itself — the viewer renders 「共 1 行」 for a 600-row
  // export and looks like a successful parse. Real exports hit this: inch marks and
  // sizes in job titles / company names (`3" cable order`, `24" monitor`), and an
  // unpaired quote is malformed CSV that a viewer must survive rather than mangle.
  let atFieldStart = true;

  for (let i = 0; i < src.length; i++) {
    const c = src[i];

    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++; // consume the escaped pair
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
      continue;
    }

    if (c === '"' && atFieldStart) {
      inQuotes = true;
      atFieldStart = false;
    } else if (c === ',') {
      row.push(field);
      field = '';
      atFieldStart = true;
    } else if (c === '\n' || c === '\r') {
      // Treat CRLF as one terminator; a lone \r also ends the row.
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      atFieldStart = true;
    } else {
      // Includes a mid-field `"`, which is kept as literal content.
      field += c;
      atFieldStart = false;
    }
  }

  // Flush the last field/row unless the file ended on a clean newline, which
  // would otherwise yield a spurious row of one empty cell.
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows;
}
